import React from "react";

const problemGuides = {
  technical: {
    title: "Technical Issue",
    description: (
      <>
        <p>
          Use this option if you’re experiencing bugs, broken features, or app
          malfunctions.
        </p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>
            Explain what exactly isn’t working (e.g., button doesn’t respond).
          </li>
          <li>Mention what you expected to happen.</li>
          <li>If possible, list steps to reproduce the issue.</li>
          <li>Include the browser/device you used.</li>
        </ul>
      </>
    ),
  },
  billing: {
    title: "Billing Problem",
    description: (
      <>
        <p>
          Use this option for payment errors, incorrect charges, or missing
          invoices.
        </p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>
            Describe what the issue is (e.g., double charge, failed payment).
          </li>
          <li>Include invoice numbers or transaction IDs if available.</li>
          <li>Let us know the date and payment method used.</li>
        </ul>
      </>
    ),
  },
  access: {
    title: "Account Access",
    description: (
      <>
        <p>For login issues, password problems, or permission errors.</p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Describe what you tried (e.g., password reset).</li>
          <li>Include any error messages seen.</li>
          <li>Mention if it happens consistently or intermittently.</li>
        </ul>
      </>
    ),
  },
  performance: {
    title: "Performance Issues",
    description: (
      <>
        <p>Report slowness, lagging, or unresponsive features.</p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Indicate what part of the app is slow.</li>
          <li>Mention the time of day or specific actions triggering it.</li>
          <li>
            Include your device type and internet connection details if helpful.
          </li>
        </ul>
      </>
    ),
  },
  data: {
    title: "Data Discrepancy",
    description: (
      <>
        <p>
          For missing, incorrect, or duplicated data (e.g., records, reports,
          transactions).
        </p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Specify what’s wrong and where you saw it.</li>
          <li>
            Include any IDs, timestamps, or screenshots that show the problem.
          </li>
          <li>Mention what the correct data should be, if known.</li>
        </ul>
      </>
    ),
  },
  other: {
    title: "Other",
    description: (
      <>
        <p>If your issue doesn’t fit a category, describe it clearly here.</p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Give as much detail as possible.</li>
          <li>
            If it relates to multiple areas (billing + data), mention both.
          </li>
        </ul>
      </>
    ),
  },
};

export default function ReportProblemGuide({ problemType }) {
  const guide = problemGuides[problemType];

  if (!guide) {
    return (
      <div className="p-4 rounded-xl border border-gray-300 bg-gray-50 text-sm text-gray-600">
        <p>Please select a problem type to see reporting instructions.</p>
      </div>
    );
  }

  return (
    <div className="p-6 rounded-md border border-primary bg-primary-light">
      <h2 className="text-lg font-semibold mb-2">
        {guide.title} – Reporting Instructions
      </h2>
      <div className="text-sm text-gray-700 space-y-2">{guide.description}</div>

      <hr className="my-4" />

      <div className="text-sm text-gray-700">
        <h3 className="font-medium mb-1">Add a Description</h3>
        <ul className="list-disc list-inside space-y-1">
          <li>Clearly explain what happened and when.</li>
          <li>Include any error messages or codes seen.</li>
          <li>If it’s inconsistent, mention how often it occurs.</li>
        </ul>

        <h3 className="font-medium mt-4 mb-1">Attach an Image (Optional)</h3>
        <ul className="list-disc list-inside space-y-1">
          <li>Screenshots or help us diagnose faster.</li>
          <li>Highlight or annotate the problem if possible.</li>
          <li>Don’t include sensitive information unless necessary.</li>
        </ul>
      </div>
    </div>
  );
}
